import { Component, HostListener, Inject, Injectable, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/// Opens Talk to us's pop-up from anywhere (the seller bar, the PrimeCommerce menu). The pop-up itself sits once at the
/// app's top level (app.component.html): inside the seller bar, the bar's slide-away transform kept it under the
/// language note (seen on a phone, 2026-11-02).
@Injectable({ providedIn: 'root' })
export class TalkService {
    /// The form showing, or null when the pop-up is closed.
    readonly open$ = new BehaviorSubject<TalkKind | null>(null);
    open(kind: TalkKind = 'seller-question'): void { this.open$.next(kind); }
    close(): void { this.open$.next(null); }

    /// A page-text or plan link that should open the pop-up: "/contact#talk" or "#talk" (the seller pages' Talk to us), and
    /// "#enterprise-form" when this page has no enquiry form of its own (the Enterprise plan's button on /sell).
    kindFor(href: string, document: Document): TalkKind | null {
        const h = (href || '').trim();
        if (h === '#talk' || /^\/(?:[a-z]{2}\/)?contact#talk$/.test(h)) { return 'seller-question'; }
        if (h === '#enterprise-form' && !document.getElementById('enterprise-form')) { return 'enterprise'; }
        return null;
    }
}

export type TalkKind = 'seller-question' | 'enterprise';

/// "Talk to us" on the seller pages (the owner, 2026-11-02): the seller-question form in a pop-up instead of the shop's
/// Contact page. It posts like the other seller forms (SellerLeads, kind "seller-question"), so the owner reads it in
/// Seller leads and gets the email. On a phone it fills the screen; Esc, the backdrop and the close button close it.
@Component({
    selector: 'app-talk-dialog',
    template: `
        <div class="talk" *ngIf="kind" role="dialog" aria-modal="true" [attr.aria-label]="'lead-talk-title' | t:'Talk to us'" (click)="close()">
            <div class="talk__panel" (click)="$event.stopPropagation()">
                <button type="button" class="talk__close" appClick (click)="close()" [attr.aria-label]="'close' | t:'Close'">&times;</button>
                <app-lead-form [kind]="kind"></app-lead-form>
            </div>
        </div>
    `,
    styles: [`
        .talk { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: flex-start; justify-content: center;
            padding: 48px 16px; overflow-y: auto; background: rgba(15, 23, 42, .55); -webkit-overflow-scrolling: touch;
            color: #101828; text-align: left; }
        .talk__panel { position: relative; width: 100%; max-width: 640px; margin: auto 0; background: #fff; border-radius: 12px;
            box-shadow: 0 20px 50px rgba(15, 23, 42, .25); }
        .talk__panel ::ng-deep .lead-form { margin: 0; border: 0; background: transparent; padding: 24px; }
        .talk__close { position: absolute; top: 8px; right: 8px; z-index: 1; width: 40px; height: 40px; border: 0; border-radius: 50%;
            background: transparent; font-size: 28px; line-height: 1; color: #475467; cursor: pointer; }
        .talk__close:hover { background: #f2f4f7; }
        @media (max-width: 575px) {
            .talk { padding: 0; }
            .talk__panel { min-height: 100%; max-width: none; border-radius: 0; box-shadow: none; }
            .talk__panel ::ng-deep .lead-form { padding: 56px 16px 24px; }
        }
    `]
})
export class TalkDialogComponent implements OnInit, OnDestroy {
    kind: TalkKind | null = null;
    private destroy$ = new Subject<void>();

    constructor(private talk: TalkService, @Inject(DOCUMENT) private document: Document, @Inject(PLATFORM_ID) private platformId: any) { }

    ngOnInit(): void {
        this.talk.open$.pipe(takeUntil(this.destroy$)).subscribe(kind => {
            this.kind = kind;
            // The page behind does not scroll while the pop-up is open.
            if (isPlatformBrowser(this.platformId)) { this.document.body.style.overflow = kind ? 'hidden' : ''; }
        });
    }

    @HostListener('document:keydown.escape')
    close(): void {
        if (this.kind) { this.talk.close(); }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
